import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { reasonCodeService, CreateReasonCodeRequest } from '../../../../../services/reasonCodeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ReasonCodeCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const ReasonCodeCreateModal: React.FC<ReasonCodeCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState<CreateReasonCodeRequest>({
        companyNo: user?.companyNo || '001',
        reasonCodeNo: 0,
        reasonDescription: '',
        reasonType: null,
        externalID: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.reasonCodeNo || formData.reasonCodeNo <= 0) {
            setError(t('manager.modules.adminMovement.reasonCode.validation.reasonCodeRequired', {}, 'Årsakskode er obligatorisk og må være et positivt tall'));
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await reasonCodeService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminMovement.reasonCode.createError', {}, 'Feil ved opprettelse av årsakskode'));
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateReasonCodeRequest>(key: K, value: CreateReasonCodeRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminMovement.reasonCode.createTitle', {}, 'Ny årsakskode')}</h2>
                            <p className="text-sm text-green-100">{t('manager.modules.adminMovement.reasonCode.createSubtitle', {}, 'Opprett en ny årsakskode')}</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">{t('manager.modules.adminMovement.reasonCode.createdSuccess', {}, 'Årsakskode opprettet!')}</p>
                                </div>
                            </div>
                        )}

                        {/* Grunndata */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminMovement.fields.masterData', {}, 'Grunndata')}</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminMovement.reasonCode.label', {}, 'Årsakskode')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.reasonCodeNo || ''}
                                        onChange={(e) => updateField('reasonCodeNo', e.target.value ? parseInt(e.target.value) : 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        autoFocus
                                        placeholder={t('manager.modules.adminMovement.reasonCode.reasonCodePlaceholder', {}, 'F.eks. 1')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminMovement.fields.description', {}, 'Beskrivelse')}</label>
                                    <input
                                        type="text"
                                        value={formData.reasonDescription || ''}
                                        onChange={(e) => updateField('reasonDescription', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder={t('manager.modules.adminMovement.reasonCode.descriptionPlaceholder', {}, 'Beskrivelse av årsakskoden')}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Tilleggsinformasjon */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">{t('manager.modules.adminMovement.reasonCode.additionalInfoLegend', {}, 'Tilleggsinformasjon')}</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t('manager.modules.adminMovement.reasonCode.fields.reasonType', {}, 'Årsakstype')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.reasonType ?? ''}
                                        onChange={(e) => updateField('reasonType', e.target.value ? parseInt(e.target.value) : null)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">{t('manager.modules.adminMovement.reasonCode.selectReasonType', {}, 'Velg årsakstype...')}</option>
                                        <option value={1}>{t('manager.modules.adminMovement.reasonCode.typeWarehouse', {}, 'Årsakskoder lager')}</option>
                                        <option value={2}>{t('manager.modules.adminMovement.reasonCode.typeDistribution', {}, 'Årsakskoder distribusjon')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminMovement.reasonCode.fields.externalID', {}, 'Ekstern ID')}</label>
                                    <input
                                        type="text"
                                        value={formData.externalID || ''}
                                        onChange={(e) => updateField('externalID', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder={t('manager.modules.adminMovement.reasonCode.externalIDPlaceholder', {}, 'Ekstern system-ID')}
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminMovement.creating', {}, 'Oppretter...')}
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {t('manager.modules.adminMovement.reasonCode.createButton', {}, 'Opprett årsakskode')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
